
import { defineComponent, PropType } from 'vue';
import { useStore } from 'vuex';
import { IUserResponseWithRoleBase } from '@/core/interfaces/ApiResponses';
import { useRouterCustom } from '@/core/hooks/use-router-custom';
import { usePagination } from '@/core/hooks/use-pagination';
import { useLoading } from '@/core/hooks/use-loading';
import { swalErrNotification, swalNotification } from '@/core/helpers/utils';
import { UserService } from '@/core/services/UserService';
import Setting from '@/components/shared/setting/Setting.vue';
import type { SettingAction } from '@/components/shared/setting/Setting.vue';
import ConfirmationDialog from '@/components/confirmation-dialog/ConfirmationDialog.vue';
import DoiMatKhauModal from '@/views/crafted/pages/nguoi-dung/doi-mat-khau-modal/DoiMatKhauModal.vue';
import IconPencil from '@/icons/IconPencil.vue';
import IconGear from '@/icons/IconGear.vue';
import IconTrash from '@/icons/IconTrash.vue';

export default defineComponent({
  name: 'nguoi-dung-table',

  components: { ConfirmationDialog, DoiMatKhauModal, Setting },

  emits: ['refresh'],

  props: {
    curPage: {
      type: Number,
      default: 1,
    },
    items: {
      type: Array as PropType<IUserResponseWithRoleBase[]>,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    perPage: {
      type: Number,
      default: 15,
    },
  },

  setup() {
    const { push } = useRouterCustom();
    const { indexCal } = usePagination();
    const { startLoading, endLoading } = useLoading();
    const store = useStore();

    return {
      indexCal,
      push,
      startLoading,
      endLoading,
      currentUserId: () => store.getters.currentUser?.id,
    };
  },

  data: () => ({
    isDeletingModalDisplay: false,
    deletingUser: null as IUserResponseWithRoleBase | null,
    isPasswordModalOpen: false,
    passwordUserId: 0,
    passwordUserName: '',
  }),

  methods: {
    getSettingActions(row: IUserResponseWithRoleBase): SettingAction[] {
      const isSelf = row.id === this.currentUserId();

      return [
        {
          icon: IconPencil,
          label: 'Chỉnh sửa',
          tooltip: 'Chỉnh sửa',
          any: ['data:nguoidung:sua'],
          onClick: () => this.push(`/quan-ly/nguoi-dung/cap-nhat/${row.id}`),
        },
        {
          icon: IconGear,
          label: 'Đổi mật khẩu',
          tooltip: 'Đổi mật khẩu',
          any: ['data:nguoidung:sua'],
          onClick: () => this.openPasswordModal(row),
        },
        {
          icon: IconTrash,
          label: 'Xóa',
          tooltip: isSelf ? 'Không thể xóa tài khoản đang đăng nhập' : 'Xóa',
          any: ['data:nguoidung:xoa'],
          isDisabled: isSelf,
          onClick: () => {
            if (!isSelf) this.onDeleteButtonClicked(row);
          },
        },
      ];
    },

    openPasswordModal(user: IUserResponseWithRoleBase) {
      this.passwordUserId = user.id;
      this.passwordUserName = user.ho_va_ten;
      this.isPasswordModalOpen = true;
    },

    onDeleteButtonClicked(user: IUserResponseWithRoleBase) {
      this.isDeletingModalDisplay = true;
      this.deletingUser = user;
    },

    async confirmDelete() {
      this.isDeletingModalDisplay = false;

      await this.startLoading('Đang xóa người dùng');

      try {
        if (this.deletingUser) {
          await UserService.remove(this.deletingUser.id);
        }
        await swalNotification('Xóa người dùng thành công', 'success');
        this.$emit('refresh');
      } catch (error) {
        await swalErrNotification(
          error,
          'Không thể xóa người dùng này, vui lòng thử lại'
        );
      } finally {
        await this.endLoading();
      }
    },
  },
});
